import { ticketSalesWindows, ticketSalesWindowValue } from "../utils/ticketSalesWindow";
import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
Button,
Stack,
Chip,
Snackbar,
Alert,
CircularProgress,
Pagination,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
Checkbox,
FormControlLabel,
MenuItem
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import AdminPageContainer from "../components/AdminPageContainer";
import Draw104WinnerConfiguration from "../components/Draw104WinnerConfiguration";
import {
  adminPageSx,
  contentCardSx,
  mobileActionGroupSx,
  paginationBarSx
} from "../utils/adminResponsive";

const initialForm = {
  draw_mode: "standard",
  draw_date: "",
  draw_time: "",
  ticket_price: 100,
  winner_prize_amount: "",
  configured_winner_count: 10,
  minimum_winners: 10,
  sales_close_hours: 3,
  rng_provider: "",
  pre_draw_video_url: "",
  draw_video_url: "",
  instagram_url: "",
  facebook_url: "",
  x_url: ""
};

function buildInitialForm(defaults = {}) {
  return {
    ...initialForm,
    sales_close_hours: Number(defaults.ticket_sales_close_hours_before_draw || 3),
    instagram_url: defaults.instagram_url || "",
    facebook_url: defaults.facebook_url || ""
  };
}

function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  const localDate = new Date(date.getTime() + 60 * 60000);
  return localDate.toISOString().slice(0, 16);
}

function getDateInputValue(value) {
  const dateTimeValue = toDateTimeInput(value);
  return dateTimeValue ? dateTimeValue.slice(0, 10) : "";
}

function getTimeInputValue(value) {
  const dateTimeValue = toDateTimeInput(value);
  return dateTimeValue ? dateTimeValue.slice(11, 16) : "";
}

function getDrawDatePrefix(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${day}${month}${year}`;
}

const drawFormGridSx = {
  display: "grid",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(2, minmax(0, 1fr))",
    xl: "repeat(3, minmax(0, 1fr))"
  },
  gap: 2
};

const drawFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

const drawFieldFullRowSx = {
  gridColumn: {
    xs: "auto",
    md: "span 2",
    xl: "span 3"
  }
};

export default function Draws() {
  const navigate = useNavigate();
  const [defaultSocialLinks, setDefaultSocialLinks] = useState({});
  const [formData, setFormData] = useState(initialForm);
  const [draws, setDraws] = useState([]);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [testModeConfirmed, setTestModeConfirmed] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteDraw, setDeleteDraw] = useState(null);
  const [selectedDrawIds, setSelectedDrawIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notifyAdmins, setNotifyAdmins] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const rowsPerPage = 20;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

  };

  const getCombinedDrawDateTime = () => {
    if (!formData.draw_date || !formData.draw_time) {
      return "";
    }

    return `${formData.draw_date}T${formData.draw_time}`;
  };

  const generateDrawCodePreview = (value) => {
    const datePrefix = getDrawDatePrefix(value);

    if (!datePrefix) {
      return "DDMMYY";
    }

    return datePrefix;
  };

  const loadDraws = async () => {
    setLoading(true);

    try {
      const data = await adminFetch("/api/admin/draws", {
        method: "GET"
      });

      if (data?.status) {
        const sortedDraws = [...(data.draws || [])].sort(
          (a, b) => new Date(b.draw_date) - new Date(a.draw_date)
        );
        setDraws(sortedDraws);
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  const loadDefaultSocialLinks = async () => {
    try {
      const data = await adminFetch("/api/admin/settings", {
        method: "GET"
      });

      if (data?.status && data.settings) {
        const nextDefaults = {
          instagram_url: data.settings.instagram_url || "",
          facebook_url: data.settings.facebook_url || "",
          ticket_sales_close_hours_before_draw: Number(data.settings.ticket_sales_close_hours_before_draw || 3)
        };

        setDefaultSocialLinks(nextDefaults);
        setFormData((prev) => {
          if (editingId) {
            return prev;
          }

          return buildInitialForm(nextDefaults);
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadDraws();
    loadDefaultSocialLinks();
  }, []);

  const resetForm = () => {
    setFormData(buildInitialForm(defaultSocialLinks));
    setEditingId(null);
    setTestModeConfirmed(false);
  };

  const handleRunDraw = async (drawId) => {
    if (Number(drawId) === 104) return;
    const targetDraw = draws.find((item) => Number(item.id) === Number(drawId));
    navigate(`/admin/draws/live/${drawId}`, {
      state: {
        draw: targetDraw || null
      }
    });
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteDraw(null);
    setBulkDeleteOpen(false);
    setDeletePassword("");
    setNotifyUsers(false);
    setNotifyAdmins(false);
  };

  const handleDeleteDraw = async () => {
    const isBulk = bulkDeleteOpen;
    if ((!isBulk && !deleteDraw) || (isBulk && !selectedDrawIds.length) || !deletePassword) return;
    setDeleting(true);
    const data = await adminFetch(isBulk ? "/api/admin/draws/bulk" : `/api/admin/draws/${deleteDraw.id}`, {
      method: "DELETE",
      body: JSON.stringify({ password: deletePassword, draw_ids: isBulk ? selectedDrawIds : undefined, notify_users: notifyUsers, notify_admins: notifyAdmins })
    });
    setDeleting(false);
    if (!data?.status) {
      showSnackbar(data?.message || "Failed to delete draw", "error");
      return;
    }
    showSnackbar(data.message || "Draw deleted successfully.", data.notification_failures ? "warning" : "success");
    closeDeleteDialog();
    if (isBulk) setSelectedDrawIds([]);
    if (editingId && (isBulk ? selectedDrawIds.includes(editingId) : Number(editingId) === Number(deleteDraw?.id))) resetForm();
    await loadDraws();
  };

  const handleSubmit = async () => {
    if (Number(editingId) === 104) return;
    const previousMode = draws.find((draw) => Number(draw.id) === Number(editingId))?.draw_mode || "standard";
    const requiresTestConfirmation = formData.draw_mode === "test" && (!editingId || previousMode !== "test");
    if (requiresTestConfirmation && !testModeConfirmed) {
      showSnackbar("Confirm the real financial processing warning before saving Test Mode", "error");
      return;
    }
    setSaving(true);

    try {
      const endpoint = editingId ? `/api/admin/draws/${editingId}` : "/api/admin/draws";
      const method = editingId ? "PUT" : "POST";

      const data = await adminFetch(endpoint, {
        method,
        body: JSON.stringify({
          ...formData,
          confirm_test_mode: requiresTestConfirmation && testModeConfirmed,
          draw_date: formData.draw_date,
          draw_time: formData.draw_time
        })
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to save draw", "error");
        setSaving(false);
        return;
      }

      showSnackbar(editingId ? "Draw updated successfully" : "Draw created successfully");
      resetForm();
      await loadDraws();
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }

    setSaving(false);
  };

  const handleEdit = (draw) => {
    setEditingId(draw.id);
    setTestModeConfirmed(false);
    if (Number(draw.id) === 104) return;
    setFormData({
      draw_mode: draw.draw_mode || "standard",
      draw_date: getDateInputValue(draw.draw_date),
      draw_time: getTimeInputValue(draw.draw_date),
      ticket_price: draw.ticket_price || 100,
      winner_prize_amount: draw.winner_prize_amount || "",
      configured_winner_count: draw.configured_winner_count || 10,
      minimum_winners: draw.minimum_winners || 10,
      sales_close_hours: draw.sales_close_hours || 3,
      rng_provider: draw.rng_provider || "",
      pre_draw_video_url: draw.pre_draw_video_url || "",
      draw_video_url: draw.draw_video_url || "",
      instagram_url: draw.instagram_url || "",
      facebook_url: draw.facebook_url || "",
      x_url: draw.x_url || ""
    });
    setPage(1);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "upcoming":
        return <Chip label="Upcoming" sx={{ bgcolor: "#1976d2", color: "#fff" }} />;
      case "live":
        return <Chip label="Live" sx={{ bgcolor: "#2e7d32", color: "#fff" }} />;
      case "completed":
        return <Chip label="Completed" sx={{ bgcolor: "#6d4c41", color: "#fff" }} />;
      default:
        return <Chip label={status} />;
    }
  };

  const filteredDraws = draws.filter((draw) => {
    if (modeFilter !== "all" && (draw.draw_mode || "standard") !== modeFilter) return false;
    const searchValue = search.toLowerCase();
    const formattedDate = new Date(draw.draw_date).toLocaleDateString().toLowerCase();

    return (
      draw.draw_code?.toLowerCase().includes(searchValue) ||
      formattedDate.includes(searchValue) ||
      String(draw.id).includes(searchValue)
    );
  });

  const totalPages = Math.ceil(filteredDraws.length / rowsPerPage) || 1;

  const paginatedDraws = filteredDraws.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const drawColumns = [
    {
      key: "select",
      label: "Select",
      render: (draw) => (
        <Checkbox
          checked={selectedDrawIds.includes(draw.id)}
          disabled={draw.status === "live"}
          inputProps={{ "aria-label": `Select draw ${draw.draw_code}` }}
          onChange={(event) => setSelectedDrawIds((previous) => event.target.checked
            ? [...previous, draw.id]
            : previous.filter((id) => id !== draw.id))}
        />
      )
    },
    {
      key: "draw_mode",
      label: "Mode",
      render: (draw) => <Chip size="small" label={draw.draw_mode === "test" ? "TEST MODE" : "STANDARD"}
        color={draw.draw_mode === "test" ? "warning" : "default"} />
    },
    {
      key: "draw_code",
      label: "Code",
      render: (draw) => draw.draw_code
    },
    {
      key: "draw_date",
      label: "Date",
      render: (draw) => formatAdminDateTime(draw.draw_date)
    },
    {
      key: "winner_prize_amount",
      label: "Prize",
      render: (draw) => `₦${Number(draw.winner_prize_amount || 0).toLocaleString()}`
    },
    {
      key: "configured_winner_count",
      label: "Configured Winners",
      render: (draw) => Number(draw.configured_winner_count || 0).toLocaleString()
    },
    {
      key: "winner_display_text",
      label: "Display Winners",
      render: (draw) => draw.winner_display_text
    },
    {
      key: "prize_pool",
      label: "Sales",
      render: (draw) => `₦${Number(draw.prize_pool || 0).toLocaleString()}`
    },
    {
      key: "total_tickets",
      label: "Purchased Tickets",
      render: (draw) => Number(draw.total_tickets || 0).toLocaleString()
    },
    {
      key: "eligible_tickets",
      label: "Eligible Tickets",
      render: (draw) => Number(draw.eligible_tickets || 0).toLocaleString()
    },
    {
      key: "status",
      label: "Status",
      render: (draw) => getStatusChip(draw.status)
    },
    {
      key: "actions",
      label: "Action",
      isActions: true,
      mobileValueSx: mobileActionGroupSx,
      desktopCellSx: { minWidth: 180 },
      render: (draw) => (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" onClick={() => handleEdit(draw)}>
            Edit
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={Number(draw.id) === 104 || draw.status === "completed" || saving}
            onClick={() => handleRunDraw(draw.id)}
          >
            Run Draw
          </Button>
          <Button size="small" variant="contained" color="error" disabled={draw.status === "live" || saving} onClick={() => setDeleteDraw(draw)} sx={{ color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}>
            Delete Draw
          </Button>
        </Stack>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Draw Management
        </Typography>

        <Card sx={{ p: { xs: 2, sm: 3, lg: 4 }, mb: 3, borderRadius: 3, ...contentCardSx }}>
          {Number(editingId) === 104 ? (
            <Draw104WinnerConfiguration
              draw={draws.find((draw) => Number(draw.id) === 104)}
              onClose={resetForm}
              onVerified={(configuration) => setDraws((previous) => previous.map((draw) => Number(draw.id) === 104
                ? { ...draw, configured_winner_count: configuration.configured_winner_count }
                : draw))}
            />
          ) : <>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            {editingId ? "Modify Draw Settings" : "Create New Draw"}
          </Typography>

          <Stack spacing={2.5}>
            <Box sx={drawFormGridSx}>
              <TextField select label="Draw Mode" value={formData.draw_mode}
                onChange={(event) => { updateField("draw_mode", event.target.value); setTestModeConfirmed(false); }}
                fullWidth sx={drawFieldSx}>
                <MenuItem value="standard">Standard Draw</MenuItem>
                <MenuItem value="test">Test Mode Draw</MenuItem>
              </TextField>
              {formData.draw_mode === "test" && <Box sx={drawFieldFullRowSx}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  TEST MODE hides this draw from public Results only. The draw still uses the normal draw process and may create real winner, wallet, payout, tax and notification records.
                </Alert>
                <FormControlLabel control={<Checkbox checked={testModeConfirmed}
                  onChange={(event) => setTestModeConfirmed(event.target.checked)} />}
                  label="I understand and confirm the real financial processing risk" />
              </Box>}
              <TextField
                label="Generated Draw Code"
                value={generateDrawCodePreview(getCombinedDrawDateTime())}
                fullWidth
                disabled
                sx={{ ...drawFieldSx, ...drawFieldFullRowSx }}
              />

              <TextField
                label="Draw Date"
                type="date"
                value={formData.draw_date}
                onChange={(e) => updateField("draw_date", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={drawFieldSx}
              />

              <TextField
                label="Draw Time (WAT)"
                type="time"
                value={formData.draw_time}
                onChange={(e) => updateField("draw_time", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                sx={drawFieldSx}
              />

              <TextField
                label="Ticket Price"
                type="number"
                value={formData.ticket_price}
                onChange={(e) => updateField("ticket_price", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="Prize Per Winner"
                type="number"
                value={formData.winner_prize_amount}
                onChange={(e) => updateField("winner_prize_amount", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="Configured Winners"
                type="number"
                value={formData.configured_winner_count}
                onChange={(e) => updateField("configured_winner_count", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="Minimum Winners"
                type="number"
                value={formData.minimum_winners}
                onChange={(e) => updateField("minimum_winners", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                select
                label="Ticket Sales Close Before Draw"
                value={ticketSalesWindowValue(formData.sales_close_hours)}
                onChange={(e) => updateField("sales_close_hours", Number(e.target.value))}
                fullWidth
                sx={drawFieldSx}
                helperText="Per-draw override. New draws start with the global setting."
              >
                {ticketSalesWindows.map(({value,label}) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
              </TextField>

              <TextField
                label="RNG Provider"
                value={formData.rng_provider}
                onChange={(e) => updateField("rng_provider", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="Pre-Draw Video URL"
                value={formData.pre_draw_video_url}
                onChange={(e) => updateField("pre_draw_video_url", e.target.value)}
                fullWidth
                helperText="Optional per-draw override; otherwise the uploaded platform pre-draw video is used."
                sx={{ ...drawFieldSx, ...drawFieldFullRowSx }}
              />

              <TextField
                label="Draw Presentation Video URL"
                value={formData.draw_video_url}
                onChange={(e) => updateField("draw_video_url", e.target.value)}
                fullWidth
                helperText="Optional per-draw override; otherwise the uploaded platform draw animation is used."
                sx={{ ...drawFieldSx, ...drawFieldFullRowSx }}
              />

              <TextField
                label="Instagram URL"
                value={formData.instagram_url}
                onChange={(e) => updateField("instagram_url", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="Facebook URL"
                value={formData.facebook_url}
                onChange={(e) => updateField("facebook_url", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

              <TextField
                label="X URL"
                value={formData.x_url}
                onChange={(e) => updateField("x_url", e.target.value)}
                fullWidth
                sx={drawFieldSx}
              />

            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: Colors.accent,
                  color: "#003300",
                  fontWeight: 700,
                  width: { xs: "100%", sm: "auto" }
                }}
                onClick={handleSubmit}
                disabled={saving || (formData.draw_mode === "test" && !editingId && !testModeConfirmed)}
              >
                {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : editingId ? "Update Draw" : "Create Draw"}
              </Button>

              {editingId && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancel Edit
                </Button>
              )}
            </Stack>
          </Stack>
          </>}
        </Card>

        <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...contentCardSx }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Existing Draw Sessions
          </Typography>

          <Button
            variant="contained"
            color="error"
            disabled={!selectedDrawIds.length || saving}
            onClick={() => setBulkDeleteOpen(true)}
            sx={{ mb: 2, color: "#fff", "&:hover": { bgcolor: "#b71c1c" } }}
          >
            Delete Selected Draws ({selectedDrawIds.length})
          </Button>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by draw ID or date"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            size="small"
            fullWidth
            sx={{ ...drawFieldSx, maxWidth: { xs: "100%", md: 360 } }}
          />
          <TextField select label="Mode" value={modeFilter} size="small"
            onChange={(event) => { setModeFilter(event.target.value); setPage(1); }}
            sx={{ ...drawFieldSx, maxWidth: { xs: "100%", sm: 190 } }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="test">Test Mode</MenuItem>
          </TextField>
          </Stack>

          <Box sx={{ position: "relative", minHeight: 380, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <ResponsiveDataTable
              columns={drawColumns}
              rows={paginatedDraws}
              loading={loading}
              minWidth={960}
              emptyMinHeight={160}
              emptyMessage={search ? "No draws match your search" : "No draws created yet"}
              mobileTitle={(draw) => draw.draw_code}
            />

            <TableLoadingOverlay loading={loading} />
          </Box>

          {!loading && filteredDraws.length > rowsPerPage && (
            <Stack sx={paginationBarSx}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Page {page} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_event, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": { color: "text.primary" },
                  "& .Mui-selected": {
                    bgcolor: `${Colors.accent} !important`,
                    color: "#003300 !important"
                  }
                }}
              />
            </Stack>
          )}
        </Card>
      </AdminPageContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={Boolean(deleteDraw) || bulkDeleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="sm">
        <DialogTitle>{bulkDeleteOpen ? `Delete ${selectedDrawIds.length} Selected Draws?` : `Delete Draw ${deleteDraw?.draw_code}?`}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This removes the draw from operational screens while preserving financial and audit history. Existing active tickets will be cancelled; refunds are not issued automatically.
          </Alert>
          {bulkDeleteOpen && (
            <Stack sx={{ mb: 2, maxHeight: 180, overflowY: "auto" }}>
              {draws.filter((draw) => selectedDrawIds.includes(draw.id)).map((draw) => <Typography key={draw.id}>{draw.draw_code}</Typography>)}
            </Stack>
          )}
          <TextField
            label="Admin Password"
            type="password"
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            fullWidth
            autoComplete="current-password"
            sx={{ mb: 2 }}
          />
          <Typography fontWeight={700} sx={{ mb: 1 }}>Notification Options</Typography>
          <Stack>
            <FormControlLabel control={<Checkbox checked={notifyUsers} onChange={(event) => setNotifyUsers(event.target.checked)} />} label="Notify affected users" />
            <FormControlLabel control={<Checkbox checked={notifyAdmins} onChange={(event) => setNotifyAdmins(event.target.checked)} />} label="Notify active admins" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteDraw} disabled={deleting || !deletePassword}>
            {deleting ? "Deleting..." : bulkDeleteOpen ? "Delete Selected" : "Delete Draw"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
